import React from "react";
import { Package } from "lucide-react";
import { DaySheet, patientName, dispositionLine } from "../providerApi";
import { Notice, Section, StatusBadge } from "./FieldPrimitives";
import StockLoadPanel from "./StockLoadPanel";

export default function DeviceReadiness({ sheet, offline = false }: { sheet: DaySheet; offline?: boolean }) {
  const residents = sheet.fitResidents;
  return (
    <Section
      title="Device readiness"
      subtitle="Approved to dispense and actually dispensed are separate facts."
    >
      <div className="fw-panel-content">
        <Notice>
          ATD approval does not record a dispense, move stock, or create a
          commission. Scheduling owns the delivery plan.
        </Notice>
      </div>
      <ul className="fw-detail-list">
        {residents
          ? residents.map((r) => (
              <li key={r.patient_id}>
                <Package />
                <div>
                  <strong>{patientName(r)}</strong>
                  <p>
                    Room {r.room_number || "not recorded"} ·{" "}
                    {dispositionLine(r.disposition)}
                  </p>
                  {r.devices.map((d) => (
                    <p key={d.id}>
                      {d.ear || "Ear not recorded"} ·{" "}
                      {d.hearing_aid_type || "Type not recorded"} ·{" "}
                      {d.serial_number
                        ? `Serial ${d.serial_number}`
                        : "Serial not yet assigned"}
                    </p>
                  ))}
                </div>
                <StatusBadge tone="amber">Waiting to fit</StatusBadge>
              </li>
            ))
          : sheet.fits.map((d) => (
              <li key={d.id}>
                <Package />
                <div>
                  <strong>{patientName(d)}</strong>
                  <p>
                    Room {d.room_number || "not recorded"} ·{" "}
                    {d.ear || "Ear not recorded"} ·{" "}
                    {d.serial_number || "Serial not yet assigned"}
                  </p>
                </div>
              </li>
            ))}
      </ul>
      {(residents ? residents.length === 0 : sheet.fits.length === 0) && (
        <div className="fw-empty">
          <Package />
          <h3>No devices waiting in this projection</h3>
          <p>Readiness reflects the facility’s current day sheet.</p>
        </div>
      )}
      {!!sheet.dispensedElsewhere?.length && (
        <div className="fw-panel-content">
          <Notice>
            {sheet.dispensedElsewhere.length} device record(s) remain
            undelivered on the board but are recorded dispensed by CounselEAR or
            a provider record. Reconcile these records before recording another
            dispense.
          </Notice>
        </div>
      )}
      {/* 2026-10-22: load devices from the provider's own stock for this building's ATD residents (a plan only). */}
      <StockLoadPanel facilityId={sheet.facility.id} offline={offline} />
    </Section>
  );
}
